import { Capacitor } from '@capacitor/core';
import type { YbugSettings } from 'ybug-react';

/**
 * iOS detection — covers both the native Capacitor app and any iOS-WebKit
 * browser. Capacitor returns 'web' for any browser context including iOS
 * Safari/Chrome/Firefox, so the UA fallback catches those. iPadOS 13+
 * reports as Mac in UA but is touch-enabled, so the touch check covers it.
 */
export const isIOS = (() => {
  if (Capacitor.getPlatform() === 'ios') return true;
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (typeof document !== 'undefined' && ua.includes('Mac') && 'ontouchend' in document) {
    return true;
  }
  return false;
})();

/**
 * Inject CSS into the Ybug widget iframe to hide the "Add screenshot" link
 * on the comment form. The iframe (#__ybug-app) has no `src=` attribute, so
 * it inherits our origin and `contentDocument` is reachable from JS — but
 * each document has its own stylesheet scope, so global.scss rules don't
 * cascade into it. The fix is to appendChild a <style> into the iframe's
 * own <head>.
 *
 * onopen fires when the user clicks the launcher and Ybug mounts the form
 * inside the iframe — that's when contentDocument.head exists. The style
 * is idempotent (id-guarded) so repeat opens are no-ops.
 */
export const injectYbugIOSStyle = () => {
  const iframe = document.getElementById('__ybug-app') as HTMLIFrameElement | null;
  const doc = iframe?.contentDocument;
  if (!doc?.head || doc.getElementById('hp-ybug-ios-style')) return;
  const style = doc.createElement('style');
  style.id = 'hp-ybug-ios-style';
  // Target each broken-on-iOS Ybug affordance by href. Ybug reuses the
  // `annotate-link` class for attachments too, so we have to match by
  // href rather than class to avoid hiding attachments as a side effect.
  // Add new hrefs to this list as more Ybug iOS-breakage surfaces.
  //   /screenshot      — html2canvas capture; broken on iOS WebKit.
  //   /screen-recorder — getDisplayMedia capture; not implemented on iOS.
  // Both still render (the recorder shows as `is-disabled` and the
  // screenshot link is a no-op), so users hit dead-ends if not hidden.
  // Two separate rules, not one comma-list. Per legacy CSS selector-list
  // parsing, an unknown selector (`:has()` on browsers that don't support
  // it) invalidates the ENTIRE rule it appears in — even other selectors
  // in the same comma list. Splitting keeps the fallback alive everywhere
  // and layers the parent-hide on top only where `:has()` is honored.
  style.textContent = `
    /* Fallback: hide just the <a> on browsers without :has() support
     * (legacy WebViews, older browsers). Leaves the wrapper div's margin
     * but at least the affordance is gone. */
    a.annotate-link[href="/screenshot"],
    a.annotate-link[href="/screen-recorder"] {
      display: none !important;
    }

    /* Modern: hide the wrapper div too so the mb-2 margin doesn't leave
     * a visible gap. Supported on iOS Safari 15.4+, Chrome 105+, Firefox
     * 121+ — i.e. essentially every browser since late 2023. */
    div:has(> a.annotate-link[href="/screenshot"]),
    div:has(> a.annotate-link[href="/screen-recorder"]) {
      display: none !important;
    }
  `;
  doc.head.appendChild(style);
};

/**
 * Ybug runtime settings — a mix of always-on overrides and iOS-only ones.
 * Spread these into BOTH
 *   1) the `<YbugProvider settings={...}>` prop in App.tsx (covers anonymous
 *      users who never trigger ybug.init()), AND
 *   2) the `ybug.init({...})` call in InitYbug (covers authenticated users —
 *      ybug.init() REPLACES window.ybug_settings, so any settings only
 *      registered via the YbugProvider prop get wiped after auth).
 *
 * Why both: `onopen` is read once at Ybug script init and stays sticky, so
 * the screenshot-link hide keeps working after replacement. But `skip_to`
 * is read fresh from window.ybug_settings on each launcher open — once
 * ybug.init() drops it, the type/annotation chooser comes back. Spreading
 * here keeps every flow directive present whichever path sets settings.
 *
 * `onbeforescreenshot` isn't in ybug-react's typed surface but Ybug's
 * runtime accepts it; we cast at the iOS spread.
 */
export const ybugSettings: Partial<YbugSettings> = {
  // Always-on: nudge users toward enough detail for repro across all
  // platforms. The key name is from YbugTranslationKey in ybug-react.
  translate: {
    'feedbackForm.TextPlaceholder':
      'Write a comment or describe a problem. Please include enough detail for us to reproduce, attaching screenshots is also very helpful.',
  },
  // iOS-only: skip the annotation chooser (skip_to), hide the broken
  // screenshot/screen-recorder affordances on form open (onopen), and
  // cancel any screenshot capture that bypasses the CSS hide
  // (onbeforescreenshot — undocumented in ybug-react's type def, hence
  // the cast on the spread itself).
  ...((isIOS
    ? ({
        skip_to: 'feedback',
        onopen: injectYbugIOSStyle,
        onbeforescreenshot: () => Promise.resolve(false),
      } as Partial<YbugSettings>)
    : {}) as Partial<YbugSettings>),
};
